// Front/src/utils/reportFormValidation.js

export const getFieldValidationClass = (name, value, formData) => {
    const d = formData.dValue;

    // Linearity and Repeatability Loads
    if (
        name.startsWith("linearityLoad") ||
        name.startsWith("linearityUnload") ||
        name.startsWith("repeatLoad")
    ) {
        if (!isPositiveNumber(value) || !isDivisibleByD(value, d)) {
            return "invalid-input";
        }
    }

    // Corner Loads
    if (
        ["cornerTopLeft", "cornerTopRight", "cornerBottomLeft", "cornerBottomRight"].includes(name)
    ) {
        if (!isPositiveNumber(value) || !isDivisibleByD(value, d)) {
            return "invalid-input";
        }
    }

    // Tare Load
    if (name === "loadValue" && formData.tareTest) {
        if (!isDivisibleByD(value, d)) {
            return "invalid-input";
        }
    }

    return ""; // valid by default
};

const isDivisibleByD = (value, dValue) => {
    const num = parseFloat(value);
    const d = parseFloat(dValue);
    if (isNaN(num) || isNaN(d) || d === 0) return false;

    // for debugging
    // console.log("d = ", d, "num = ", num);

    const quotient = num / d;
    return Math.abs(quotient - Math.round(quotient)) < 1e-9;
};

export const isPositiveNumber = (value) => {
    // Check if the value is a positive number
    return value !== "" && !isNaN(value) && Number(value) >= 0;
};

export const validateTestValues = (testType, formData, totalRows) => {
    for (let i = 1; i <= totalRows; i++) {
        const weight = formData[`${testType}Weight${i}`];
        const load = formData[`${testType}Load${i}`];
        const unload = testType === "linearity" ? formData[`${testType}Unload${i}`] : null;

        if (!isPositiveNumber(weight)) {
            return { valid: false, errorKey: `${testType}WeightInvalid` };
        }

        if (!isPositiveNumber(load)) {
            return { valid: false, errorKey: `${testType}LoadInvalid` };
        }

        if (!isDivisibleByD(load, formData.dValue)) {
            return { valid: false, errorKey: `${testType}LoadNotDivisibleByD` };
        }

        if (testType === "linearity") {
            if (!isPositiveNumber(unload)) {
                return { valid: false, errorKey: `${testType}UnloadInvalid` };
            }

            if (!isDivisibleByD(unload, formData.dValue)) {
                return { valid: false, errorKey: `${testType}UnloadNotDivisibleByD` };
            }
        }
    }

    return { valid: true };
};


export const validateFormData = (formData, t) => {
    // Dynamically determine the number of rows
    const defaultRows = 6;
    const extraRows = 10;
    const linearityTotalRows = formData.linearityAddMoreLines ? extraRows : defaultRows;
    let repeatabilityTotalRows;
    if (formData.accuracyClass === 3 || formData.accuracyClass === 4) {
        repeatabilityTotalRows = 3;
    } else if (formData.repeatabilityAddMoreLines) {
        repeatabilityTotalRows = extraRows;
    } else {
        repeatabilityTotalRows = defaultRows;
    }

    // for debugging
    //console.log("formData.accuracyClass = ", formData.accuracyClass);
    //console.log("repeatabilityTotalRows = ", repeatabilityTotalRows);

    // Ensure Scale Serial ID is not empty
    if (!formData.scaleSerialId.trim()) {
        return t.emptyScaleSerial;
    }

    // Validate Linearity Test values
    if (formData.linearityTest) {
        const result = validateTestValues("linearity", formData, linearityTotalRows);
        if (!result.valid) {
            return t[result.errorKey] || t.linearityInvalid;
        }
    }

    // Validate Repeatability Test values
    if (formData.repeatabilityTest) {
        const result = validateTestValues("repeat", formData, repeatabilityTotalRows);
        if (!result.valid) {
            return t[result.errorKey] || t.repeatabilityInvalid;
        }
    }

    // Validate Corners Test values (if selected)
    if (formData.cornersTest) {
        const cornerWeight = formData.cornerWeightValue;
        const cornerLoads = [
            formData.cornerTopLeft,
            formData.cornerTopRight,
            formData.cornerBottomLeft,
            formData.cornerBottomRight
        ];

        // Ensure Weight Value is a positive number
        if (!isPositiveNumber(cornerWeight)) {
            return t.cornersWeightInvalid;
        }

        // Ensure all Load Read values are positive numbers
        if (cornerLoads.some(load => !isPositiveNumber(load))) {
            return t.cornersLoadInvalid;
        }

        // Ensure all Load Read values are divisible by d
        if (cornerLoads.some(load => !isDivisibleByD(load, formData.dValue))) {
            return t.cornersLoadNotDivisibleByD;
        }
    }

    // Validate Tare Test values (if selected)
    if (formData.tareTest && (formData.loadValue >= 0 || !isPositiveNumber(formData.weightValue))) {
        return t.tareInvalid;
    }

    // Validate Tare Test load value is divisible by dValue (if selected)
    if (formData.tareTest && !isDivisibleByD(formData.loadValue, formData.dValue)) {
        return t.tareNotDivisibleByD;
    }

    // Check if Next Calibration Months is a valid number (between 1 and 36)
    if (!isPositiveNumber(formData.nextCalibrationMonths) || formData.nextCalibrationMonths < 1 || formData.nextCalibrationMonths > 36) {
        return t.calibrationInvalid;
    }

    // Check if temperature and humidity are within the allowed range
    const isMarketingReport = formData.reportType === "Marketing";

    // Define acceptable ranges based on report type
    const tempMin = isMarketingReport ? 15 : 10;
    const tempMax = isMarketingReport ? 29 : 40;
    const humidityMin = 30;
    const humidityMax = 70;
    
    // Validate temperature and humidity
    const tempInvalid =
        formData.temperatureStart < tempMin || formData.temperatureEnd < tempMin ||
        formData.temperatureStart > tempMax || formData.temperatureEnd > tempMax;
    
    const humidityInvalid =
        formData.humidityStart < humidityMin || formData.humidityEnd < humidityMin ||
        formData.humidityStart > humidityMax || formData.humidityEnd > humidityMax;
    
    if (
        !formData.temperatureStart || !formData.temperatureEnd || 
        !formData.humidityStart || !formData.humidityEnd || 
        tempInvalid || humidityInvalid
    ) {
        return isMarketingReport ? t.marketingTempHumidityInvalid : t.tempHumidityInvalid;
    }

    // Validate Loading Permission (if checked, must have a positive number)
    if (formData.useLoadingPermission && (!isPositiveNumber(formData.loadingPermissionValue))) {
        return t.loadingPermissionInvalid;
    }

    // Validate Check Up To (if checked, must have a positive number)
    if (formData.useCheckUpTo && (!isPositiveNumber(formData.checkUpToValue))) {
        return t.checkUpToInvalid;
    }

    // Calibration approval declaration is mandatory
    if (!formData.calibrationApproval) {
        return t.calibrationApprovalRequired;
    }

    // Validate that eValue >= dValue
    const d = parseFloat(formData.dValue);
    const e = parseFloat(formData.eValue);
    if (!isNaN(d) && !isNaN(e) && e < d) {
        return t.eLowerThanD;
    }

    return null; // No validation errors
};

export const validateScaleData = (formData, t) => {
    if (!formData.serialNumber.trim()) {
        return t.scale_serial_required;
    }
    if (!formData.internalId.trim()) {
        return t.scale_internal_id_required;
    }
    if (!formData.clientId.trim()) {
        return t.scale_client_id_required;
    }
    if (!formData.catalogNumber.trim()) {
        return t.scale_catalog_required;
    }
    if (!isPositiveNumber(formData.maxValue)) {
        return t.scale_max_invalid;
    }
    if (!isPositiveNumber(formData.minValue)) {
        return t.scale_min_invalid;
    }
    if (!isPositiveNumber(formData.dValue)) {
        return t.scale_d_invalid;
    }
    if (!isPositiveNumber(formData.eValue)) {
        return t.scale_e_invalid;
    }
    if (!isPositiveNumber(formData.nValue)) {
        return t.scale_n_invalid;
    }
    if (!String(formData.accuracyClass).trim()) {
        return t.scale_accuracy_required;
    }
    if (!String(formData.weightUnits).trim()) {
        return t.scale_weight_units_required;
    }

    const d = parseFloat(formData.dValue);
    const e = parseFloat(formData.eValue);
    if (!isNaN(d) && !isNaN(e) && e < d) {
        return t.eLowerThanD;
    }

    return null;
};
